import { message } from "antd";
import { RcFile } from "antd/es/upload";
import { Dayjs } from "dayjs";
import { ApplicationDocumentsToSign } from "../../components/DocumentsSignModal/types/types";
import { IChangeApplicationBankOnlyStatusResponse } from "../../interfaces/store/api/APIInterfaces";
import { Response } from "../../services/api/types/type";
import {
  ApplicationBanks,
  IApplications,
  StatusObj,
} from "../slice/authSliceLogin/authSliceLogin";
import {
  Document,
  DocumentType,
  IBankForAdd,
  IinitialStateRequestAllData,
} from "../slice/requestAppSlise/requestAppSlise";
import { api } from "./apiCore";
import { ModesDocumentVerificationAfterSigning } from "./types/applicationApiTypes";

type TArchiveBank = { bank_id: number; is_enabled: boolean; app_id: number };

export type TResponseApplicationData = Omit<
  IinitialStateRequestAllData,
  "MATCH_DATA"
>;

type TChangeStatusReaquest = { app_id: number; type: string };

type TAddApplicationBank = { bank_id: number[]; app_id: number };

export type TEditApplicationBank = {
  bank_id: number;
  app_id: number;
  status_id?: number;
  released_at?: string | null | Dayjs;
  platform_app_id?: number | string;
  overstatement?: string | number;
  base_commission?: string;
};

export type TEditApplication = {
  application_sum_bg: string;
  app_id: number;
  initial_price: number;
  total_sum: string;
  sum_avance: number;
  start_bg_at: string;
  finish_bg_at: string;
  sum_bg: string;
  sum_bg_execution: string;
  sum_bg_obligation: string;
  banks: IBankForAdd[];
};

export interface IGetFilteredListApplication {
  organization_inn?: string;
  status_preset?: string;
  application_id?: string;
  limit: number;
  page: number;
  purchase_number?: string;
}

export interface IResponseDataType {
  applications: { data: IApplications[]; last_page: number };
  bank_statuses: {
    color: string;
    id: number;
    info: string;
    info_color: string;
    title: string;
  }[];
  types_bg: {
    id: number;
    name: string;
  }[];
}
interface IResponseChangeStatus {
  message: string;
  success: boolean;
  data: StatusObj;
}

// запросы для работы с заявкой
export const applicationsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // получение данных о заявке
    getApplicationData: builder.query<TResponseApplicationData, number>({
      query: (applicationId) => ({
        url: `/auth/applications/${applicationId}`,
      }),
      providesTags: (result, error, applicationId) => [
        { type: "Application", applicationId },
      ],
    }),
    // получение списка заявок
    getListApplicationPage: builder.query<IResponseDataType, number>({
      query: (value) => ({
        url: `auth/applications?page=${value}`,
      }),
      transformResponse: (response: { data: IResponseDataType }) =>
        response.data,
      providesTags: ["ListApplication"],
    }),
    // изменения статуса заявки
    changeApplicationStatus: builder.mutation<
      IResponseChangeStatus,
      TChangeStatusReaquest
    >({
      query: (value) => ({
        url: `/auth/applications/${value.app_id}/status`,
        method: "PATCH",
        body: { type: value.type },
      }),
      invalidatesTags: ["Application"],
    }),
    // добавление банка в заявку
    addApplicationBanks: builder.mutation<
      ApplicationBanks[],
      TAddApplicationBank
    >({
      query: (value) => ({
        url: `/auth/applications/${value.app_id}/bank`,
        method: "POST",
        body: {
          bank_id_arr: value.bank_id,
        },
      }),
      transformResponse: (response: { data: ApplicationBanks[] }) =>
        response.data,
      invalidatesTags: ["Application"],
    }),
    // добавить банк в архив в заявке
    applicationBankArchive: builder.mutation<TArchiveBank, TArchiveBank>({
      query: (value) => ({
        url: `/auth/applications/${value.app_id}/bank/${value.bank_id}/enable`,
        method: "PATCH",
        body: {
          is_enabled: value.is_enabled,
        },
      }),
      invalidatesTags: ["Application"],
    }),
    // изменение банка в заявке
    editApplicationBank: builder.mutation<
      { data: ApplicationBanks },
      TEditApplicationBank
    >({
      query: (value) => {
        const body = {} as TEditApplicationBank;

        if (value.status_id !== null) {
          body.status_id = value.status_id;
        }
        if (value.released_at !== null) {
          body.released_at = value.released_at;
        }
        if (value.platform_app_id) {
          body.platform_app_id = String(value.platform_app_id);
        }
        if (value.overstatement !== null) {
          body.overstatement = value.overstatement;
        }
        if (value.base_commission !== null) {
          body.base_commission = value.base_commission;
        }

        return {
          url: `/auth/applications/${value.app_id}/bank/${value.bank_id}`,
          method: "PUT",
          body: body,
        };
      },
      invalidatesTags: ["Application"],
    }),
    // получение списка заявок по фильтру
    getFilteredListApplication: builder.query<
      IResponseDataType,
      IGetFilteredListApplication
    >({
      query: ({ page, limit, ...value }) => {
        let url = "auth/applications";

        const params: IGetFilteredListApplication = {
          status_preset: value.status_preset?.length
            ? value.status_preset
            : undefined,
          organization_inn: value.organization_inn?.length
            ? value.organization_inn
            : undefined,
          purchase_number: value?.purchase_number?.length
            ? value?.purchase_number
            : undefined,
          application_id: value.application_id?.length
            ? value.application_id
            : undefined,
          limit: limit,
          page: page,
        };

        return {
          url: url,
          headers: { Authorization: "Bearer " + localStorage.access_token },
          params: params,
        };
      },

      transformResponse: (response: { data: IResponseDataType }) =>
        response.data,
    }),
    // изменение заявки
    editApplication: builder.mutation<unknown, TEditApplication>({
      query: (value) => {
        if (!value.app_id) {
          return { url: `` };
        }
        return {
          url: `/auth/applications/${value.app_id}/edit`,
          method: "PUT",
          body: {
            initial_price: value.initial_price,
            total_sum: value.total_sum,
            sum_avance: value.sum_avance,
            start_bg_at: value.start_bg_at,
            finish_bg_at: value.finish_bg_at,
            sum_bg: value.sum_bg,
            sum_bg_execution: value.sum_bg_execution,
            sum_bg_obligation: value.sum_bg_obligation,
            banks: value.banks,
          },
        };
      },
      invalidatesTags: ["Application"],
    }),
    // удаление документа из заявки
    deleteDocumentApplication: builder.mutation<unknown, { id: number }>({
      query: (document) => {
        return { url: `/auth/file/${document.id}`, method: "DELETE" };
      },
    }),
    getApplicationDocumentsToSign: builder.query<
      Response<ApplicationDocumentsToSign>,
      number
    >({
      query: (applicationId) => ({
        method: "POST",
        url: "auth/sign/client-anketa/application/signature",
        body: { appId: applicationId },
      }),
    }),
    //Добавление документов от банка (кроме КамКомБанка)
    setDocumentToBank: builder.mutation<
      any,
      {
        //documents: DocumentType<Document>[];

        applicationId: number;
        typeDocId: number;
        file: string | Blob | RcFile;
        bank_id: number;
      }
    >({
      query: ({ applicationId, typeDocId, file, bank_id }) => {
        const bodyFormData = new FormData();
        bodyFormData.append("type_doc_id", String(typeDocId));
        bodyFormData.append("file", file);
        bodyFormData.append("bank_id", String(bank_id));
        return {
          url: `auth/file/${applicationId}`,
          method: "POST",
          body: bodyFormData,
          formData: true,
        };
      },
      transformResponse: (response) => response.data,
      async onQueryStarted({ applicationId }, { dispatch, queryFulfilled }) {
        // Не костыль, а понятный код
        try {
          const { data: uploadedDocument } = await queryFulfilled;
          dispatch(
            applicationsApi.util.updateQueryData(
              "getApplicationData",
              applicationId,
              (draft) => {
                // Ищем индекс банка, к которому прикреплена заявка
                const bankIndex = draft?.data?.application_banks.findIndex(
                  (bank) => bank.bank_id === Number(uploadedDocument.bank_id),
                );
                // Ищем id типа документа
                const documentIndex = draft.data.application_banks[
                  bankIndex
                ].documents.findIndex(
                  (draftedDoc) =>
                    draftedDoc.id === Number(uploadedDocument.type_doc_id),
                );
                // Обновляем файл документа по индексу банка и id типа документа
                draft.data.application_banks[bankIndex].documents[
                  documentIndex
                ] = uploadedDocument;
              },
            ),
          );
        } catch (err) {
          if (err instanceof Error) message.error(err.message);
        }
      },
    }),
    //Обновление документов от банка (кроме КамКомБанка)
    updateDocumentToBank: builder.mutation<
      any,
      {
        documents: DocumentType<Document>[];
        index: number;
        applicationId: number;
        typeDocId: number;
        file: string | Blob | RcFile;
        bank_id: number;
      }
    >({
      query: ({ documents, index, file, bank_id, typeDocId }) => {
        const bodyFormData = new FormData();
        bodyFormData.append("type_doc_id", String(typeDocId));
        bodyFormData.append("file", file);
        bodyFormData.append("bank_id", String(bank_id));
        return {
          url: `auth/file/${documents[index]?.document?.id}/update`,
          method: "POST",
          body: bodyFormData,
          formData: true,
        };
      },
      transformResponse: (response) => response.data,
      async onQueryStarted({ applicationId }, { dispatch, queryFulfilled }) {
        // Не костыль, а понятный код
        try {
          const { data: uploadedDocument } = await queryFulfilled;
          dispatch(
            applicationsApi.util.updateQueryData(
              "getApplicationData",
              applicationId,
              (draft) => {
                // Ищем индекс банка, к которому прикреплена заявка
                const bankIndex = draft?.data?.application_banks.findIndex(
                  (bank) => bank.bank_id === Number(uploadedDocument.bank_id),
                );
                // Ищем id типа документа
                const documentIndex = draft.data.application_banks[
                  bankIndex
                ].documents.findIndex(
                  (draftedDoc) =>
                    draftedDoc.id === Number(uploadedDocument.type_doc_id),
                );
                // Обновляем файл документа по индексу банка и id типа документа
                draft.data.application_banks[bankIndex].documents[
                  documentIndex
                ] = uploadedDocument;
              },
            ),
          );
          message.success("Документ обновлен");
        } catch (err) {
          message.success("Не удалось обновить документ");
        }
      },
    }),
    // верификация прикрепленных документов к заявке после подписания
    verificationDocsSignature: builder.mutation<
      IChangeApplicationBankOnlyStatusResponse,
      {
        applicationBankId: number | string;
        mode: ModesDocumentVerificationAfterSigning;
      }
    >({
      query: (value) => ({
        url: `auth/applications/system-bank/create-data-to-bank/${value.applicationBankId}/processing/${value.mode}`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetApplicationDataQuery,
  useChangeApplicationStatusMutation,
  useAddApplicationBanksMutation,
  useApplicationBankArchiveMutation,
  useEditApplicationBankMutation,
  useGetListApplicationPageQuery,
  useGetFilteredListApplicationQuery,
  useEditApplicationMutation,
  useDeleteDocumentApplicationMutation,
  useGetApplicationDocumentsToSignQuery,
  useSetDocumentToBankMutation,
  useUpdateDocumentToBankMutation,
  useVerificationDocsSignatureMutation,
  //lazy
  useLazyGetListApplicationPageQuery,
  useLazyGetFilteredListApplicationQuery,
} = applicationsApi;
